import { DOCUMENT } from '@angular/common'
import { Injectable, OnDestroy, inject, signal } from '@angular/core'
import * as Sentry from '@sentry/angular'
import { environment } from '../../../environments/environment'
import { ChunkRecoveryTracker, CHUNK_SPIKE_WINDOW_MS, isChunkLoadError } from '../handlers/chunk-load-error'
import { PosthogService } from './posthog.service'

@Injectable({ providedIn: 'root' })
export class ChunkLoadErrorService implements OnDestroy {
  private document = inject(DOCUMENT)
  private posthog = inject(PosthogService)
  private tracker = new ChunkRecoveryTracker()
  private readonly visibleState = signal(false)
  private readonly secondsState = signal(5)
  private readonly pausedState = signal(false)
  private timer?: ReturnType<typeof setInterval>

  readonly visible = this.visibleState.asReadonly()
  readonly secondsRemaining = this.secondsState.asReadonly()
  readonly paused = this.pausedState.asReadonly()

  isChunkLoadError(error: unknown): boolean {
    return isChunkLoadError(error)
  }

  handleChunkLoadError(): void {
    // Router and global handlers can report the same failure.
    if (this.visible()) return
    this.reportSpike()
    this.showUpdatePrompt()
  }

  showUpdatePrompt(): void {
    if (this.visible()) return
    this.visibleState.set(true)
    this.secondsState.set(5)
    this.pausedState.set(false)
    this.timer = setInterval(() => {
      this.secondsState.update(seconds => seconds - 1)
      if (this.secondsRemaining() <= 0) this.refresh()
    }, 1000)
  }

  pause(): void {
    this.clearTimer()
    this.pausedState.set(true)
  }

  refresh(): void {
    this.clearTimer()
    this.document.defaultView?.location.reload()
  }

  reset(): void {
    this.clearTimer()
    this.visibleState.set(false)
    this.secondsState.set(5)
    this.pausedState.set(false)
  }

  ngOnDestroy(): void {
    this.clearTimer()
  }

  private reportSpike(): void {
    let storage: Storage | undefined
    try {
      storage = this.document.defaultView?.sessionStorage
    } catch {
      // Some browsers disallow session storage.
    }
    const count = this.tracker.record(storage)
    if (count === null) return
    const error = new Error('Dashboard asset recovery spike')
    const properties = { recoveryAttempts: count, windowMs: CHUNK_SPIKE_WINDOW_MS, scope: 'browser_tab' }
    this.posthog.captureException(error, properties)
    if (environment.production) {
      try {
        Sentry.captureException(error, {
          level: 'warning',
          fingerprint: ['dashboard-asset-recovery-spike'],
          tags: { errorType: 'AssetRecoverySpike' },
          extra: properties,
        })
      } catch {
        // Monitoring must never interfere with recovery.
      }
    }
  }

  private clearTimer(): void {
    if (this.timer !== undefined) clearInterval(this.timer)
    this.timer = undefined
  }
}
